import React, { FC, useRef, useEffect, useCallback, useState } from 'react';
import Dropdown from './components/dropdown';
import TextSearch from './components/textSearch';
import { TCountry, TDealerLocatorSearchProps, SearchType, TState } from '../../types/dealerLocatorLanding.types';
import SearchTypeSwitch, { SwitchOption } from './components/searchTypeSwitch';
import { fetchCountryDealersByGeoFilter, fetchDealersByGeoCode, fetchDealersByGeoFilter } from '../../dealerLocator.utils';
import { windowService } from '../../../../utils/window.utils.js';
import { iconSize } from '../../../../constants/icon.constants';
import Icon from '../../../@shared/icon';
import { useTranslation } from 'react-i18next';
import { USER_ABORTED_REQUEST_CODE } from '../../../../constants/dealerLocator.constants';
import { googleClickDealerLocatorResults, googleGeolocationClick } from '../../../../utils/analytics';
import { dealerLocatorGeolocationClick, dealerLocatorResultsOpen } from '../../../../constants/analytics.constants';
import DealerLocatorGeoLocatorToastMessage from '../dealerLocatorGeoLocatorToastMessage';
import './styles.scss';

const DealerLocatorSearch: FC<TDealerLocatorSearchProps> = ({
  model,
  onDealerResults,
  country,
  setCountry,
  region,
  setRegion,
  autocompleteService,
  placesService,
  setUserLocation,
  setIsLoadingDealers,
  filteredDealerResults,
  sitecoreContext,
}) => {
  const { t } = useTranslation();
  const controllerRef = useRef<AbortController | null>();
  const countryIsoCodeWithFallback =
    model.searchType === SearchType.SearchByLocationOrDealer ? model.defaultCountry : country?.IsoCode || model.defaultCountry;
  const isCountryChangedAfterPageLoad = useRef<boolean>(false);

  const onCountrySelect = (country: TCountry) => {
    setCountry(country);
    setRegion(null);
    isCountryChangedAfterPageLoad.current = true;
  };

  const onRegionSelect = (_region: TState) => {
    if (_region?.gmiCode !== region?.gmiCode) {
      setRegion(_region);
    }
  };

  useEffect(() => {
    const { Country } = country || {};
    const { name } = region || {};
    const { dealerLocatorEquipmentsResults, dealerLocatorServicesResults } = filteredDealerResults || {};

    if (model?.searchType === 'SearchByCountryAndRegion' && Country && name) {
      googleClickDealerLocatorResults(
        dealerLocatorResultsOpen,
        Country,
        undefined,
        dealerLocatorEquipmentsResults,
        dealerLocatorServicesResults,
        name
      );
    } else if (model?.searchType === 'NoSearch') {
      googleClickDealerLocatorResults(
        dealerLocatorResultsOpen,
        Country ?? countryIsoCodeWithFallback ?? '',
        undefined,
        dealerLocatorEquipmentsResults,
        dealerLocatorServicesResults,
        undefined
      );
    }
  }, [region, filteredDealerResults]);

  const onGooglePlaceSelect = (place: any, placeId: string, placeDesc: string, updateUrl: boolean) => {
    const lat = place.geometry?.location?.lat() || '';
    const lng = place.geometry?.location?.lng() || '';

    if (lat && lng) {
      const apiCallParams = {
        lat: lat,
        lng: lng,
        pageId: model.pageId,
        language: model.siteLanguage,
        countryIsoCode: countryIsoCodeWithFallback,
      };

      setIsLoadingDealers(true);
      fetchDealersByGeoCode(apiCallParams, controllerRef)
        .then((data) => {
          const dealershipResults = data?.dealershipResults;
          const _place = {
            lat: Number.parseFloat(lat),
            lng: Number.parseFloat(lng),
          };
          onDealerResults({
            results: dealershipResults || [],
            place: _place,
            placeId: placeId,
            queryText: placeDesc,
            updateUrl: updateUrl,
          });
        })
        .catch((error) => {
          const _place = {
            lat: Number.parseFloat(lat),
            lng: Number.parseFloat(lng),
          };
          onDealerResults({
            results: [],
            place: _place,
            placeId: placeId,
          });
          console.error(error, 'Error while fetching Dealers');
        });
    }
  };

  const [showGeoLocationErrorMessage, updateShowGeoLocationErrorMessage] = useState<boolean>(false);
  const [enteredDisabledStateOnce, updateEnteredDisabledStateOnce] = useState<boolean>(false);

  const showUserLocation = useCallback(() => {
    const success = (position) => {
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      googleGeolocationClick(
        dealerLocatorGeolocationClick,
        sitecoreContext?.analytics?.['region'],
        sitecoreContext?.analytics?.['language'],
        sitecoreContext?.analytics?.['market']
      );
    };

    const error = () => {
      windowService()?.navigator.permissions.query({ name: "geolocation" }).then((permission) => {
        const permissionState = permission?.state;
        if (enteredDisabledStateOnce || permissionState === 'denied') {
          updateShowGeoLocationErrorMessage(true);
        } else {
          updateEnteredDisabledStateOnce(true);
        }
      });
    }
    windowService()?.navigator?.geolocation?.getCurrentPosition(success, error);
  }, [enteredDisabledStateOnce]);

  useEffect(() => {
    if (model.searchType !== SearchType.SearchByCountry || !isCountryChangedAfterPageLoad.current) {
      return;
    }

    const apiCallParams = {
      pageId: model.pageId,
      language: model.siteLanguage,
      countryIsoCode: countryIsoCodeWithFallback,
      country: country?.Country,
    };

    setIsLoadingDealers(true);
    fetchCountryDealersByGeoFilter(apiCallParams, controllerRef)
      .then((data) => {
        onDealerResults({
          results: data?.dealershipResults || [],
          updateUrl: true,
        });
      })
      .catch((error) => {
        if (error?.code !== USER_ABORTED_REQUEST_CODE) {
          onDealerResults({
            results: [],
          });
          console.error(error, 'Error while fetching Dealers', error.code);
        }
      });
  }, [country]);

  useEffect(() => {
    if (!region) {
      return;
    }

    const apiCallParams = {
      region: region.name,
      pageId: model.pageId,
      language: model.siteLanguage,
      countryIsoCode: countryIsoCodeWithFallback,
    };

    setIsLoadingDealers(true);
    fetchDealersByGeoFilter(apiCallParams, controllerRef)
      .then((data) => {
        onDealerResults({
          results: data?.dealershipResults || [],
          updateUrl: true,
        });
      })
      .catch((error) => {
        if (error?.code !== USER_ABORTED_REQUEST_CODE) {
          onDealerResults({
            results: [],
          });
          console.error(error, 'Error while fetching Dealers', error.code);
        }
      });
  }, [region]);

  return (
    <>
      {model.searchType !== SearchType.NoSearch && (
        <div className="dealer-locator-landing-search">
          <DealerLocatorGeoLocatorToastMessage
            showGeoLocationErrorMessage={showGeoLocationErrorMessage}
            updateShowGeoLocationErrorMessage={updateShowGeoLocationErrorMessage}
            extraClassName='dealer-locator-landing-search__error-msg'
          />
          <SearchTypeSwitch type={model.searchType}>
            <SwitchOption typeName={SearchType.SearchByCountryAndRegion}>
              <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--dropdown">
                <Dropdown
                  preSelectedItem={country}
                  items={model.countries}
                  label={t('cnhi-dealerLocatorCountry')}
                  onSelect={onCountrySelect}
                />
              </div>
              {country?.Regions && (
                <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--dropdown">
                  <Dropdown
                    preSelectedItem={region}
                    items={country.states}
                    label={t('cnhi-dealerLocatorRegion')}
                    onSelect={onRegionSelect}
                  />
                </div>
              )}
            </SwitchOption>
            <SwitchOption typeName={SearchType.SearchByLocationOrDealer}>
              <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--text-search">
                {countryIsoCodeWithFallback && (
                  <>
                    <TextSearch
                      model={model}
                      countryIsoCode={countryIsoCodeWithFallback}
                      label={t('cnhi-dealerLocatorSearchByLocationOrDealer')}
                      onDealerSelect={onDealerResults}
                      onGooglePlaceSelect={onGooglePlaceSelect}
                      autocompleteService={autocompleteService}
                      placesService={placesService}
                      filteredDealerResults={filteredDealerResults}
                    />
                    <div className="dealer-locator-landing-search__users-location-btn" onClick={showUserLocation}>
                      <Icon iconName="action-precision" color="white" size={iconSize['xlg']} />
                    </div>
                  </>
                )}
              </div>
            </SwitchOption>
            <SwitchOption typeName={SearchType.SearchByCountryAndLocationOrDealer}>
              <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--dropdown">
                <Dropdown
                  preSelectedItem={country}
                  items={model.countries}
                  label={t('cnhi-dealerLocatorCountry')}
                  onSelect={onCountrySelect}
                />
                {countryIsoCodeWithFallback && (
                  <div className="dealer-locator-landing-search__users-location-btn" onClick={showUserLocation}>
                    <Icon iconName="action-precision" color="white" size={iconSize['xlg']} />
                  </div>
                )}
              </div>
              {country && (
                <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--text-search">
                  <TextSearch
                    model={model}
                    countryIsoCode={country?.IsoCode}
                    label={t('cnhi-dealerLocatorSearchByLocationOrDealer')}
                    onDealerSelect={onDealerResults}
                    onGooglePlaceSelect={onGooglePlaceSelect}
                    autocompleteService={autocompleteService}
                    placesService={placesService}
                    filteredDealerResults={filteredDealerResults}
                  />
                </div>
              )}
            </SwitchOption>
            <SwitchOption typeName={SearchType.SearchByCountry}>
              <div className="dealer-locator-landing-search__col dealer-locator-landing-search__col--dropdown">
                <Dropdown
                  items={model.countries}
                  label={t('cnhi-searchByCountry')}
                  onSelect={onCountrySelect}
                />
              </div>
            </SwitchOption>
          </SearchTypeSwitch>
        </div>
      )}
    </>
  );
};

export default DealerLocatorSearch;
